import React, { useCallback, useEffect, useState } from 'react';
import { useFormik } from 'formik';
import DateInput from '../../components/DateInput/Index';
import { useAuth } from '../../context/AuthContext';
import server from '../../global/server';
import { useApp } from '../../context/AppContext';
import LoadingButton from '@mui/lab/LoadingButton';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import * as Yup from 'yup';
import useHandleErrors from '../../hooks/useHandleErrors';
import {
    Avatar,
    Box,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    FormControl,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import axios from 'axios';
import SelectBank from '../../components/SelectBank';
import { validaCNPJ, validaCPF } from '../../global/cpfCnpj';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Alert, Button } from '@mui/material';

const RegisterSchema = Yup.object().shape({
    document_type: Yup.string().required(),
    document: Yup.string()
        .required('Campo obrigatório')
        .when('document_type', {
            is: value => value === 'CPF',
            then: schema =>
                schema.length(11, 'Documento inválido').test('invalid-cpf', 'Documento inválido', cpf => validaCPF(cpf)),
            otherwise: schema =>
                schema.length(14, 'Documento incompleto').test('invalid-cnpj', 'Documento inválido', cnpj => validaCNPJ(cnpj)),
        }),
    holder: Yup.string().max(30, 'Máximo 30 caracteres').required('Campo obrigatório'),
    holder_type: Yup.string().required('Campo obrigatório'),
    birthdate: Yup.date().required('Campo obrigatório'),
    monthly_income: Yup.number().required('Campo obrigatório'),
    professional_occupation: Yup.string().required('Campo obrigatório'),
    zipcode: Yup.string().min(8, 'Mínimo 8 digitos').max(8, 'Máximo 8 caracteres').required('Campo obrigatório'),
    neighborhood: Yup.string().required('Campo obrigatório'),
    complement: Yup.string().required('Campo obrigatório'),
    number: Yup.string().required('Zero caso não haja.'),
    reference_point: Yup.string().max(50, 'Max 50 caracteres'),
    bank_number: Yup.string().max(3).required('Campo obrigatório'),
    agency_number: Yup.string().max(4, 'Máximo 4 digitos').test('int', 'Dado inválido', v => Number.isInteger(Number(v))).required('Campo obrigatório'),
    agency_digit: Yup.string().nullable().max(1, 'Máximo 1 digito'),
    account_number: Yup.string().max(12, 'Máximo 13 digitos').test('int', 'Dado inválido', v => Number.isInteger(Number(v))).required('Campo obrigatório'),
    account_digit: Yup.string().max(2, 'Máximo 2 digitos').test('int', 'Dado inválido', v => Number.isInteger(Number(v))).required('Campo obrigatório'),
    phone: Yup.string().required('Campo obrigatório'),
});

const SectionCard = ({ children }) => (
    <Box sx={{
        width: '100%',
        bgcolor: 'white',
        borderRadius: '16px',
        border: '1px solid #e8e8e8',
        p: { xs: '20px 16px', sm: '28px 24px' },
    }}>
        {children}
    </Box>
);

const SectionLabel = ({ children, icon }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, mt: 1 }}>
        {icon}
        <Typography variant="subtitle2" fontWeight={600} color="#555">
            {children}
        </Typography>
    </Box>
);

export default () => {
    const [search] = useSearchParams();
    const { verifySession, user, setUser } = useAuth();
    const { isOffline } = useApp();
    const [hasError, setError] = useState();
    const isEditMode = Boolean(user.profile);
    useHandleErrors(hasError);
    const navigation = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const isHostFlow = search.get('sign') === 'encontrado';
    const [submitted, setSubmitted] = useState(false);
    // Aprovacao pode ser automatica ou depender de analise: o texto do modal muda
    const [aprovadoDeImediato, setAprovadoDeImediato] = useState(false);

    const formik = useFormik({
        initialValues: {
            document_type: 'CPF',
            document: '',
            holder: '',
            holder_type: 'individual',
            birthdate: '',
            monthly_income: '',
            professional_occupation: '',
            email: user?.email || '',
            zipcode: '',
            street: '',
            complement: '',
            city: '',
            state: '',
            neighborhood: '',
            number: '',
            bank_number: '',
            agency_number: '',
            agency_digit: '',
            phone: '',
            account_number: '',
            account_digit: '',
            account_type: 'checking',
            reference_point: '',
            phone_type: 'mobile',
        },
        validationSchema: RegisterSchema,
        onSubmit: async (values, { setSubmitting }) => {
            try {
                setSubmitting(true);
                const method = user.profile ? 'put' : 'post';
                const response = await server[method]('/profile', { ...values, zipcode: Number(values.zipcode) });

                if (response.status === 200 || response.status === 201) {
                    if (!isEditMode) {
                        // Cadastro novo: confirma o fim do fluxo com o modal.
                        // Antes isso dependia do ?sign=encontrado na URL, entao
                        // quem chegava por outro caminho nao via aviso nenhum
                        setAprovadoDeImediato(Boolean(response.data?.data?.approved));
                        setSubmitted(true);
                        // O proprio POST devolve o perfil criado, entao encaixa
                        // ele no usuario em memoria em vez de gastar uma volta
                        // no servidor so para descobrir o que ja esta na mao
                        setUser(atual => ({ ...atual, profile: response.data?.data }));
                    } else {
                        verifySession();
                        enqueueSnackbar('Dados atualizados', { variant: 'success' });
                    }
                }
                setSubmitting(false);
            } catch (error) {
                setSubmitting(false);
                setError(error);
            }
        },
    });

    const [searchingZipCode, setSearching] = useState(false);
    const searchPostalCode = useCallback(async (zipcode) => {
        try {
            setSearching(true);
            const { data } = await axios.get(`https://viacep.com.br/ws/${zipcode}/json/`);
            if (data.erro) throw 'Erro';
            formik.setFieldValue('street', data.logradouro);
            formik.setFieldValue('city', data.localidade);
            formik.setFieldValue('state', data.uf);
            formik.setFieldValue('neighborhood', data.bairro);
        } catch {
            formik.setFieldValue('street', '');
            formik.setFieldValue('city', '');
            formik.setFieldValue('state', '');
            formik.setFieldValue('neighborhood', '');
            formik.setFieldError('zipcode', 'CEP inválido ou não encontrado.');
        } finally {
            setSearching(false);
        }
    }, []);

    useEffect(() => {
        const zipcode = String(formik.values.zipcode).replace(/\D/g, '');
        if (zipcode.length === 8) searchPostalCode(zipcode);
    }, [formik.values.zipcode]);

    useEffect(() => {
        if (user?.profile) {
            const profileData = { ...user.profile };
            if (profileData.birthdate) {
                profileData.birthdate = new Date(profileData.birthdate).toISOString().split('T')[0];
            }
            formik.setValues(profileData, true);
        }
    }, [user]);

    const f = (name, label, props = {}) => (
        <TextField
            label={label}
            name={name}
            size="small"
            fullWidth
            value={formik.values[name]}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={isEditMode && !['phone', 'reference_point', 'complement'].includes(name)}
            error={formik.touched[name] && Boolean(formik.errors[name])}
            helperText={formik.touched[name] && formik.errors[name]}
            {...props}
        />
    );

    // Fecha so pelo OK: sem onClose o clique no fundo nao dispensa o modal
    const modalConclusao = (
        <Dialog
            open={submitted}
            disableEscapeKeyDown
            PaperProps={{ sx: { borderRadius: '16px', maxWidth: 440 } }}
        >
            <DialogContent sx={{ textAlign: 'center', p: { xs: 3, sm: 4 } }}>
                <CheckCircleOutlineIcon sx={{ fontSize: 64, color: '#2e7d32', mb: 2 }} />
                <Typography variant="h6" fontWeight={700} gutterBottom>
                    Parabéns, você concluiu seu cadastro de anfitrião!
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
                    {aprovadoDeImediato
                        ? `Seu cadastro já está aprovado e seus encontros ficarão visíveis para
                           todos os usuários. Agora é só criar o seu primeiro encontro. Bom proveito!`
                        : `Seus dados foram enviados com sucesso. Nossa equipe fará a análise do
                           cadastro e dos dados de recebimento, e você será notificado assim que o
                           processo for concluído.`}
                </Typography>
                <Button
                    variant="contained"
                    fullWidth
                    onClick={() => navigation('/lista')}
                    sx={{
                        bgcolor: '#006a61',
                        borderRadius: '8px',
                        height: 44,
                        textTransform: 'none',
                        fontWeight: 600,
                        '&:hover': { bgcolor: '#005a52' },
                    }}
                >
                    OK
                </Button>
            </DialogContent>
        </Dialog>
    );

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            maxWidth: 720,
            mx: 'auto',
            p: { xs: '16px 12px 32px', sm: '24px 16px 48px' },
            gap: 3,
        }}>
            {/* Header */}
            <Box sx={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, pt: 1 }}>
                <Avatar
                    src={user?.profileImage}
                    alt={user?.name}
                    sx={{ width: 80, height: 80, border: '3px solid #e0e0e0', fontSize: '2rem' }}
                >
                    {user?.name?.[0]}
                </Avatar>
                <Box>
                    <Typography variant="h6" fontWeight={600} sx={{ lineHeight: 1.3 }}>
                        {isEditMode ? 'Dados de Recebimento' : 'Cadastrar Dados de Recebimento'}
                    </Typography>
                    {isHostFlow && !isEditMode && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            Última etapa para se tornar anfitrião
                        </Typography>
                    )}
                </Box>
                {isHostFlow && (
                    <Chip
                        icon={<PersonOutlineIcon sx={{ fontSize: 16 }} />}
                        label="Etapa 2 de 2"
                        size="small"
                        sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 500, '& .MuiChip-icon': { color: 'inherit' } }}
                    />
                )}
            </Box>

            {isHostFlow && !isEditMode && (
                <Alert
                    icon={<InfoOutlinedIcon sx={{ fontSize: 20 }} />}
                    severity="info"
                    sx={{ width: '100%', borderRadius: '12px', '& .MuiAlert-message': { fontSize: '0.85rem' } }}
                >
                    Após o envio dos seus dados, nossa equipe realizará uma análise para aprovação do seu cadastro como anfitrião e dos dados de recebimento. Esse processo pode levar alguns dias.
                </Alert>
            )}

            <form onSubmit={(e) => {
                e.preventDefault();
                formik.setTouched(Object.fromEntries(Object.keys(formik.values).map(k => [k, true])));
                formik.handleSubmit();
            }} style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 24 }}>

                {/* Dados Pessoais */}
                <SectionCard>
                    <SectionLabel icon={<PersonOutlineIcon sx={{ fontSize: 18, color: '#006a61' }} />}>
                        Informações pessoais
                    </SectionLabel>
                    <Stack spacing={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Tipo do documento</InputLabel>
                            <Select
                                value={formik.values.document_type}
                                label="Tipo do documento"
                                name="document_type"
                                disabled={isEditMode}
                                onChange={formik.handleChange}
                            >
                                <MenuItem value="CPF">CPF</MenuItem>
                                <MenuItem value="CNPJ">CNPJ</MenuItem>
                            </Select>
                        </FormControl>
                        {f('document', formik.values.document_type === 'CPF' ? 'CPF' : 'CNPJ', { inputProps: { maxLength: 14 } })}
                        {f('holder', formik.values.document_type === 'CPF' ? 'Nome completo' : 'Razão Social', { inputProps: { maxLength: 30 }, helperText: formik.touched.holder && formik.errors.holder ? formik.errors.holder : 'Máximo 30 caracteres' })}
                        <FormControl fullWidth size="small">
                            <InputLabel>Tipo do Titular</InputLabel>
                            <Select
                                value={formik.values.holder_type}
                                label="Tipo do Titular"
                                name="holder_type"
                                onChange={formik.handleChange}
                                disabled={isEditMode}
                            >
                                <MenuItem value="individual">Individual</MenuItem>
                                <MenuItem value="corporation">Corporation</MenuItem>
                            </Select>
                        </FormControl>
                        <DateInput
                            label="Data de Nascimento"
                            name="birthdate"
                            obrigatorio
                            naoFuturo
                            value={formik.values.birthdate}
                            onChange={iso => formik.setFieldValue('birthdate', iso)}
                            onBlur={formik.handleBlur}
                            disabled={isEditMode && !['phone', 'reference_point', 'complement'].includes('birthdate')}
                            erro={formik.touched.birthdate && Boolean(formik.errors.birthdate)}
                            textoAuxiliar={formik.touched.birthdate ? formik.errors.birthdate : ''}
                        />
                        {f('monthly_income', 'Renda Mensal', { type: 'number' })}
                        {f('professional_occupation', 'Ocupação Profissional')}
                        <FormControl fullWidth size="small">
                            <InputLabel>Tipo de Telefone</InputLabel>
                            <Select
                                value={formik.values.phone_type}
                                label="Tipo de Telefone"
                                name="phone_type"
                                onChange={formik.handleChange}
                            >
                                <MenuItem value="mobile">Celular</MenuItem>
                                <MenuItem value="landline">Fixo</MenuItem>
                                <MenuItem value="other">Outro</MenuItem>
                            </Select>
                        </FormControl>
                        {f('phone', 'Telefone do recebedor')}
                    </Stack>
                </SectionCard>

                {/* Endereco */}
                <SectionCard>
                    <SectionLabel icon={<PersonOutlineIcon sx={{ fontSize: 18, color: '#006a61' }} />}>
                        Endereço
                    </SectionLabel>
                    <Stack spacing={2}>
                        <TextField
                            label="CEP"
                            name="zipcode"
                            size="small"
                            fullWidth
                            value={formik.values.zipcode}
                            onChange={(e) => formik.setFieldValue('zipcode', e.target.value.replace(/\D/g, '').slice(0, 8))}
                            onBlur={formik.handleBlur}
                            error={formik.touched.zipcode && Boolean(formik.errors.zipcode)}
                            inputProps={{ maxLength: 8 }}
                            InputProps={{
                                endAdornment: searchingZipCode ? (
                                    <InputAdornment position="end"><CircularProgress size={16} /></InputAdornment>
                                ) : null,
                            }}
                            helperText={
                                formik.touched.zipcode && formik.errors.zipcode
                                    ? formik.errors.zipcode
                                    : formik.values.street ? `${formik.values.street}, ${formik.values.city}-${formik.values.state}` : ''
                            }
                        />
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                            {f('street', 'Rua')}
                            {f('neighborhood', 'Bairro')}
                            {f('city', 'Cidade')}
                            {f('state', 'Estado')}
                        </Box>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '3fr 1fr' }, gap: 2 }}>
                            {f('complement', 'Complemento')}
                            {f('number', 'Numero', { type: 'number' })}
                        </Box>
                        {f('reference_point', 'Ponto de Referência')}
                    </Stack>
                </SectionCard>

                {/* Dados Bancarios */}
                <SectionCard>
                    <SectionLabel icon={<AccountBalanceIcon sx={{ fontSize: 18, color: '#006a61' }} />}>
                        Dados bancários
                    </SectionLabel>
                    <Stack spacing={2}>
                        <SelectBank
                            name="bank_number"
                            value={formik.values.bank_number}
                            onChange={formik.setFieldValue}
                            error={formik.touched.bank_number && Boolean(formik.errors.bank_number)}
                            helperText={formik.touched.bank_number && formik.errors.bank_number}
                        />
                        <Box sx={{ display: 'grid', gridTemplateColumns: '3fr 1fr', gap: 2 }}>
                            {f('agency_number', 'Agencia')}
                            {f('agency_digit', 'Digito')}
                        </Box>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '3fr 1fr', gap: 2 }}>
                            {f('account_number', 'Conta')}
                            {f('account_digit', 'Digito')}
                        </Box>
                        <FormControl fullWidth size="small">
                            <InputLabel>Tipo da conta</InputLabel>
                            <Select
                                value={formik.values.account_type}
                                label="Tipo da conta"
                                name="account_type"
                                onChange={formik.handleChange}
                            >
                                <MenuItem value="checking">Corrente</MenuItem>
                                <MenuItem value="savings">Poupanca</MenuItem>
                            </Select>
                        </FormControl>
                    </Stack>
                </SectionCard>

                {/* Botoes */}
                <Stack spacing={1.5}>
                    <LoadingButton
                        type="submit"
                        fullWidth
                        loading={formik.isSubmitting}
                        variant="contained"
                        disabled={formik.isSubmitting || isOffline}
                        sx={{
                            bgcolor: '#006a61',
                            borderRadius: '8px',
                            height: 44,
                            textTransform: 'none',
                            fontWeight: 500,
                            '&:hover': { bgcolor: '#005a52' },
                        }}
                    >
                        {isEditMode ? 'Atualizar' : isHostFlow ? 'Finalizar cadastro' : 'Cadastrar'}
                    </LoadingButton>

                    {isHostFlow && !isEditMode && (
                        <LoadingButton
                            onClick={() => navigation('/lista')}
                            fullWidth
                            variant="outlined"
                            sx={{
                                borderRadius: '8px',
                                height: 44,
                                textTransform: 'none',
                                fontWeight: 500,
                                color: '#666',
                                borderColor: '#ddd',
                                '&:hover': { bgcolor: '#f5f5f5', borderColor: '#bbb' },
                            }}
                        >
                            Cancelar e continuar como conviva
                        </LoadingButton>
                    )}
                </Stack>
            </form>

            {modalConclusao}
        </Box>
    );
};
